import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Global scroll-reveal: gently fades and rises each <section> into view
 * as the visitor scrolls. Non-invasive — it tags top-level sections and
 * animates them via CSS (.reveal-section / .is-visible).
 *
 * Important: on client-side navigation we FIRST reset the scroll position
 * to the top, then wait for that reset (and layout) to settle before we
 * register/observe sections. Registering mid-transition would measure the
 * previous page's scroll offset and leave the new page's above-the-fold
 * content stuck hidden (a blank/black screen).
 */
export const ScrollReveal = () => {
  const location = useLocation();

  useEffect(() => {
    let cancelled = false;

    const prefersReduced = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;

    // 1) Reset scroll to the top of the new page.
    if (window.__lenis && typeof window.__lenis.scrollTo === 'function') {
      window.__lenis.scrollTo(0, { immediate: true });
    }
    window.scrollTo(0, 0);

    // 2) Observer that reveals sections once they intersect the viewport.
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -8% 0px' }
    );

    const register = () => {
      if (cancelled) return;
      const sections = document.querySelectorAll('.App section');
      sections.forEach((el) => {
        if (el.dataset.revealInit) return;
        // Skip nested sections so we reveal the outer block only once.
        if (el.parentElement && el.parentElement.closest('.reveal-section')) {
          return;
        }
        el.dataset.revealInit = 'true';

        if (prefersReduced) {
          el.classList.add('reveal-section', 'is-visible');
          return;
        }
        el.classList.add('reveal-section');
        observer.observe(el);
      });
    };

    // 3) Register only AFTER the scroll reset + paint have settled, so the
    //    IntersectionObserver evaluates sections at the correct (top) scroll.
    //    A second pass catches any sections rendered from async data.
    const t1 = setTimeout(register, 140);
    const t2 = setTimeout(register, 700);

    return () => {
      cancelled = true;
      clearTimeout(t1);
      clearTimeout(t2);
      observer.disconnect();
    };
  }, [location.pathname]);

  return null;
};

export default ScrollReveal;
